import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { updateProduct, fetchProductDetails } from '../../../redux/slices/productsSlice';

// Optimized Input Component with memoization
const FormInput = React.memo(({ label, type = "text", name, value, onChange, required = false, ...props }) => (
  <div className="mb-6">
    <label className="block font-semibold mb-2 text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
      required={required}
      {...props}
    />
  </div>
));

// Optimized Textarea Component
const FormTextarea = React.memo(({ label, name, value, onChange, required = false, rows = 4 }) => (
  <div className="mb-6">
    <label className="block font-semibold mb-2 text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
      rows={rows}
      required={required}
    />
  </div>
));

// Image Preview Component with delete functionality
const ImagePreview = React.memo(({ image, index, onDelete }) => (
  <div className="relative group">
    <img
      src={image.url}
      alt={image.altText || "Product Image"}
      className="w-24 h-24 object-cover rounded-lg shadow-md"
      loading="lazy"
    />
    <button
      type="button"
      onClick={() => onDelete(index)}
      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      aria-label="Delete image"
    >
      ×
    </button>
  </div>
));

const EditProductPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { seletedProduct, loading, error } = useSelector((state) => state.products);

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    price: 0,
    countInStock: 0,
    sku: "",
    category: "",
    sizes: [],
    colors: [],
    collections: "",
    material: "",
    gender: "",
    images: []
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch product details on mount
  useEffect(() => {
    if (id) {
      dispatch(fetchProductDetails(id));
    }
  }, [dispatch, id]);

  // Populate form when product data is loaded
  useEffect(() => {
    if (seletedProduct) {
      setProductData(seletedProduct);
    }
  }, [seletedProduct]);

  // Memoized change handler
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setProductData((prevData) => ({ ...prevData, [name]: value }));
    setHasChanges(true);
  }, []);

  // Optimized array input handler
  const handleArrayInput = useCallback((field) => (e) => {
    const values = e.target.value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean); // Remove empty strings
    
    setProductData(prevData => ({ ...prevData, [field]: values }));
    setHasChanges(true);
  }, []);

  // Optimized image upload with better error handling
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size should be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      setUploadError(null);
      
      const { data } = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000 // 30 second timeout
        }
      );

      setProductData((prevData) => ({
        ...prevData,
        images: [...prevData.images, { url: data.imageUrl, altText: "" }],
      }));
      setHasChanges(true);
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  }, []);

  // Delete image handler
  const handleDeleteImage = useCallback((index) => {
    setProductData(prevData => ({
      ...prevData,
      images: prevData.images.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  }, []);

  // Optimized submit handler with validation
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!productData.name.trim()) {
      alert('Product name is required');
      return;
    }

    if (productData.price <= 0) {
      alert('Price must be greater than 0');
      return;
    }

    try {
      await dispatch(updateProduct({ id, productData })).unwrap();
      navigate("/admin/products");
    } catch (error) {
      console.error('Update failed:', error);
      alert('Failed to update product. Please try again.');
    }
  }, [dispatch, id, productData, navigate]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    navigate("/admin/products");
  }, [hasChanges, navigate]);

  // Loading and error states
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold mb-2">Error</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate("/admin/products")}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Edit Product</h2>
          {hasChanges && (
            <span className="text-sm text-orange-600 font-medium">Unsaved changes</span>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">Basic Information</h3>
            
            <FormInput
              label="Product Name"
              name="name"
              value={productData.name}
              onChange={handleChange}
              required
              placeholder="Enter product name"
            />

            <FormTextarea
              label="Description"
              name="description"
              value={productData.description}
              onChange={handleChange}
              required
              rows={5}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Price"
                type="number"
                name="price"
                value={productData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
              />

              <FormInput
                label="Stock Quantity"
                type="number"
                name="countInStock"
                value={productData.countInStock}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <FormInput
              label="SKU"
              name="sku"
              value={productData.sku}
              onChange={handleChange}
              placeholder="e.g., PROD-001"
            />
          </div>

          {/* Variants Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">Variants</h3>
            
            <FormInput
              label="Sizes"
              name="sizes"
              value={productData.sizes.join(", ")}
              onChange={handleArrayInput('sizes')}
              placeholder="e.g., XS, S, M, L, XL"
            />

            <FormInput
              label="Colors"
              name="colors"
              value={productData.colors.join(", ")}
              onChange={handleArrayInput('colors')}
              placeholder="e.g., Red, Blue, Green"
            />
          </div>

          {/* Images Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">Product Images</h3>
            
            <div className="mb-4">
              <label className="block font-semibold mb-2 text-gray-700">Upload Image</label>
              <input
                type="file"
                onChange={handleImageUpload}
                accept="image/*"
                disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {uploading && (
                <p className="text-blue-600 text-sm mt-2 flex items-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Uploading image...
                </p>
              )}
              {uploadError && (
                <p className="text-red-600 text-sm mt-2">{uploadError}</p>
              )}
            </div>

            {productData.images.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {productData.images.map((image, index) => (
                  <ImagePreview
                    key={index}
                    image={image}
                    index={index}
                    onDelete={handleDeleteImage}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="submit"
              disabled={!hasChanges}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Product
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductPage;
